/**
 * OptimizeCrosslinkRail — internal-linking rail rendered at the bottom of
 * each /optimize/:slug page. Links to 6 other role pages so PageRank
 * flows through the cluster instead of pooling on one page.
 *
 * Why:
 *   Google rewards tight topical clusters where every page in the cluster
 *   has 2+ internal inbound links from peer pages. Without this rail, a
 *   visitor landing on /optimize/software-engineer-resume has zero
 *   internal exits except the global nav — a wasted SEO + UX moment.
 *
 * Selection logic:
 *   Skip the current slug, return 6 deterministic neighbors so the rail
 *   is stable per role (same order on every render = caching-friendly +
 *   feels less spammy to Google than a random shuffle).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Compass } from 'lucide-react';
import { OPTIMIZE_ROLES } from '../data/optimizeRoles';

// Deterministic "next 6" — slice from the role's index forward, wrapping.
const pickRelated = (currentSlug, count = 6) => {
  const slugs = Object.keys(OPTIMIZE_ROLES);
  const i = slugs.indexOf(currentSlug);
  if (i < 0) return slugs.slice(0, count);
  const rotated = [...slugs.slice(i + 1), ...slugs.slice(0, i)];
  return rotated.slice(0, count);
};

const OptimizeCrosslinkRail = ({ currentSlug }) => {
  const related = pickRelated(currentSlug, 6);
  if (related.length === 0) return null;

  return (
    <section
      className="py-12 border-t border-[#e5e7eb] bg-white"
      data-testid="optimize-crosslink-rail"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 mb-2">
          <Compass size={16} className="text-[#002fa7]" />
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#002fa7]">
            Related roles
          </span>
        </div>
        <h2 className="font-satoshi text-xl sm:text-2xl font-bold text-[#0f1115] mb-6">
          Optimize for a different role
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {related.map((slug) => {
            const data = OPTIMIZE_ROLES[slug];
            if (!data) return null;
            return (
              <Link
                key={slug}
                to={`/optimize/${slug}`}
                className="group block p-4 border border-[#e5e7eb] hover:border-[#002fa7] hover:bg-[#f9fafb] transition-colors"
                data-testid={`crosslink-${slug}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-satoshi text-sm font-bold text-[#0f1115] group-hover:text-[#002fa7] transition-colors">
                      {data.role} resume optimization
                    </div>
                    <div className="text-xs text-[#6b7280] mt-1 line-clamp-2">
                      ATS-tuned keyword pool & {data.faqs?.length || 3} role-specific FAQs.
                    </div>
                  </div>
                  <ArrowRight
                    size={14}
                    className="text-[#9ca3af] group-hover:text-[#002fa7] flex-shrink-0 mt-0.5 transition-colors"
                  />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default OptimizeCrosslinkRail;
